/**
 * NewsPulseAlertToast — floating red banner shown on every NewsPulse
 * page when the reader's region has a live weather / crop advisory.
 *
 * Data source: /api/newspulse/v1/farmerspulse/city-strip
 * Region resolution:
 *   1. Read `np.local.cache` from localStorage (produced by /local)
 *   2. Fall back to `np.local.saved_cities[home_key]` if available
 *   3. Otherwise render nothing (no location = no alert)
 *
 * Dismissal: alert_id is stored in sessionStorage so the toast
 * doesn't nag on every page hop, but returns on the next session.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X, MapPin } from "lucide-react";

const CACHE_KEY = "np.local.cache";
const SAVED_CITIES_KEY = "np.local.saved_cities";
const HOME_KEY = "np.local.home_city_key";
const DISMISS_KEY_PREFIX = "np.local.alert_toast.dismissed:";

function readLocation() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.location) return parsed.location;
    }
  } catch { /* noop */ }
  try {
    const savedRaw = window.localStorage.getItem(SAVED_CITIES_KEY);
    const homeKey = window.localStorage.getItem(HOME_KEY);
    if (savedRaw && homeKey) {
      const list = JSON.parse(savedRaw);
      if (Array.isArray(list)) {
        for (const c of list) {
          const lat = Number(c.lat).toFixed(3);
          const lon = Number(c.lon).toFixed(3);
          const k = `${c.city || ""}|${c.admin1 || ""}|${c.country || ""}|${lat},${lon}`;
          if (k === homeKey) return c;
        }
      }
    }
  } catch { /* noop */ }
  return null;
}

function slug(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function locationIdFor(loc) {
  if (!loc) return null;
  const country = (loc.country || "IN").toUpperCase();
  const parts = [country, slug(loc.admin1), slug(loc.city)].filter(Boolean);
  return parts.join("-");
}

export default function NewsPulseAlertToast() {
  const [alert, setAlert] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const loc = readLocation();
      if (!loc) return;
      try {
        const country = (loc.country || "IN").toUpperCase();
        const locationId = locationIdFor(loc);
        const params = new URLSearchParams({ country, limit_alerts: "1" });
        if (locationId) params.set("location_id", locationId);
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ""}/api/newspulse/v1/farmerspulse/city-strip?${params.toString()}`
        );
        if (!r.ok) return;
        const d = await r.json();
        const first = (d.alerts || [])[0];
        if (!alive || !first) return;
        // Skip if this specific alert has been dismissed already this session.
        const already = window.sessionStorage.getItem(DISMISS_KEY_PREFIX + first.event_id);
        if (already) return;
        setAlert(first);
      } catch { /* silent — toast is best-effort */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!alert || dismissed) return null;

  const onDismiss = () => {
    try { window.sessionStorage.setItem(DISMISS_KEY_PREFIX + alert.event_id, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  const tag = (alert.alert_tag || "alert").toUpperCase();
  const region = alert.location?.city || alert.location?.admin1 || "";

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-40 max-w-md rounded-2xl border border-red-300 bg-red-50 text-red-900 shadow-lg animate-in slide-in-from-bottom"
      role="alert"
      data-testid="newspulse-alert-toast"
    >
      <div className="flex items-start gap-3 p-4">
        <span className="w-9 h-9 rounded-full bg-red-100 text-red-700 inline-flex items-center justify-center shrink-0">
          <AlertTriangle className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1">
            <span className="text-red-700">Weather advisory · {tag}</span>
            {region && (
              <>
                <span className="text-red-300">·</span>
                <span className="inline-flex items-center gap-0.5 text-red-800">
                  <MapPin className="w-2.5 h-2.5" /> {region}
                </span>
              </>
            )}
          </div>
          <div
            className="text-sm font-semibold leading-snug line-clamp-2"
            data-testid="newspulse-alert-toast-title"
          >
            {alert.title}
          </div>
          <Link
            to="/local"
            className="mt-2 inline-block text-xs font-semibold text-red-800 hover:text-red-900 underline underline-offset-2"
            data-testid="newspulse-alert-toast-cta"
            onClick={onDismiss}
          >
            See what's happening →
          </Link>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="w-7 h-7 rounded-full hover:bg-red-100 inline-flex items-center justify-center shrink-0"
          aria-label="Dismiss advisory"
          data-testid="newspulse-alert-toast-dismiss"
        >
          <X className="w-4 h-4 text-red-700" />
        </button>
      </div>
    </div>
  );
}
